import {
  importProvidersFrom,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
  inject,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import { DateAdapter, provideCalendar } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideServiceWorker } from '@angular/service-worker';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { provideNgxMask } from 'ngx-mask';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { registerLocaleData } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DEFAULT_OPTIONS } from '@angular/material/bottom-sheet';
import { CustomMaterialModule } from './app/util/material';
import {
  APP_BOTTOM_SHEET_DEFAULTS,
  APP_DIALOG_DEFAULTS,
} from './app/shared/ui/dialog/ui-modal-defaults';
import { DialogEscCloseService } from './app/shared/ui/dialog/dialog-esc-close.service';
import {
  DEFAULT_TABLE_PAGINATOR_CONFIG,
  TABLE_PAGINATOR_OPTIONS,
} from './app/shared/ui/table/table-paginator.config';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';
import { httpAuthInterceptor } from './app/util/services/http-auth-interceptor.service';
import { provideApi } from './app/api';
import { AuthService } from './app/util/services/auth.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlCro } from './app/util/matPaginatorIntlCroClass';
import { RouteGuard } from './app/util/guards/route.guard';
import { PreventCloseGuardService } from './app/util/guards/prevent-close.guard';
import { ThemingService } from './app/util/services/theming.service';
import { PwaService } from './app/util/pwa/pwa.service';

// Custom configuration initialization
import './assets/echarts/chalk';
import './assets/echarts/shine';

echarts.use([
  BarChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

registerLocaleData(localeDe, localeDeExtra);

export function initializeApp(authService: AuthService) {
  return () => authService.initializeAuth();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withXhr(), withInterceptors([httpAuthInterceptor])),
    // Generierter API-Client: Pfade in der Spec enthalten bereits /api/v1.
    provideApi(window.location.origin),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideCalendar({ provide: DateAdapter, useFactory: adapterFactory }),
    provideEchartsCore({ echarts }),
    importProvidersFrom(
      MatDatepickerModule,
      MatNativeDateModule,
      CustomMaterialModule,
    ),
    RouteGuard,
    PreventCloseGuardService,
    ThemingService,
    PwaService,
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlCro },
    provideNgxMask(),
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        hideMultipleSelectionIndicator: true,
      },
    },
    // Standardized modal UX: no backdrop close, no autofocus, focus restore.
    // ESC-to-close is restored app-wide by DialogEscCloseService below.
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: APP_DIALOG_DEFAULTS },
    {
      provide: MAT_BOTTOM_SHEET_DEFAULT_OPTIONS,
      useValue: APP_BOTTOM_SHEET_DEFAULTS,
    },
    {
      provide: TABLE_PAGINATOR_OPTIONS,
      useValue: DEFAULT_TABLE_PAGINATOR_CONFIG,
    },
    provideAppInitializer(() => initializeApp(inject(AuthService))()),
    provideAppInitializer(() => inject(DialogEscCloseService).init()),
  ],
}).catch((err) => console.error(err));
